import { ExEmptyState, ExIcon } from '@boomi/exosphere';
import type { Ace } from 'ace-builds';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { DebugTabId, StateValue } from '../../../../types/debugger';
import CodeEditor from '../../../generic/CodeEditor';
import Loader from '../../../loader/Loader';
import {
    DEBUG_LIST_DEFAULT_WIDTH,
    DEBUG_LIST_MAX_WIDTH_PERCENT,
    DEBUG_LIST_MIN_WIDTH,
} from '../debugPaneSizing';
import { useDebugState } from '../DebugProvider';

type MarkerType = 'text' | 'fullLine' | 'screenLine' | Ace.MarkerRenderer;
type ValueType = 'valueElementId' | 'typeElementPropertyId';
type Marker = { type: ValueType; id: string; line: number }[];
type MarkerLine = { [key: string]: Marker };

// Scans forward from startIndex (in place, no copying) for the next line containing
// contentValue, falling back to startIndex if none follows.
const getContentValueLineIndex = (lines: string[][], startIndex: number) => {
    for (let i = startIndex; i < lines.length; i++) {
        if (lines[i][0].includes('contentValue')) {
            return i;
        }
    }

    return startIndex;
};

const StateValues = (_: { screen?: DebugTabId }) => {
    const { callStack: callStackItems } = useDebugState();

    const [hasStateValuesChanged, setHasStateValuesChanged] = useState(false);
    const [selectedValueElementId, setSelectedValueElementId] = useState<string | null>(null);
    const [listWidth, setListWidth] = useState(DEBUG_LIST_DEFAULT_WIDTH);
    const innerRef = useRef<HTMLDivElement>(null);
    const activeResizeCleanupRef = useRef<(() => void) | null>(null);

    const selectedCallStackItem = callStackItems.length
        ? callStackItems.find((callStackItem) => callStackItem.isSelected)
        : null;

    const stateValues = selectedCallStackItem ? selectedCallStackItem.stateValues : null;
    const breakpointsHit = selectedCallStackItem ? selectedCallStackItem.breakpointsHit : null;

    const selectedStateValue =
        stateValues?.find((stateValue) => stateValue.valueElementId === selectedValueElementId) ??
        stateValues?.[0] ??
        null;

    // This is used to determine which line in the code editor should
    // have a marker applied to indicate a breakpoint has been hit.
    const breakLines: MarkerLine = useMemo(() => {
        const result: MarkerLine = {};
        stateValues?.forEach((sv) => {
            const lines = JSON.stringify(sv, null, '\t').split('\n');
            // JSON.stringify with an indent puts each key on its own line as `"key": value,`.
            // Split each line once at its first colon to get [key, value] so specific keys can be
            // located by line index - a value containing a colon (e.g. a URL) must not split further.
            const keyValueLines = lines.map((line) => {
                const colonIndex = line.indexOf(':');
                return colonIndex === -1
                    ? [line]
                    : [line.slice(0, colonIndex), line.slice(colonIndex + 1)];
            });
            const markers: Marker = [];
            keyValueLines.forEach((line, index) => {
                if (line[0].includes('valueElementId')) {
                    markers.push({
                        type: 'valueElementId',
                        id: line[1].replace(/"|,/g, '').trim(),
                        line: getContentValueLineIndex(keyValueLines, index),
                    });
                }

                if (line[0].includes('typeElementPropertyId')) {
                    markers.push({
                        type: 'typeElementPropertyId',
                        id: line[1].replace(/"|,/g, '').trim(),
                        line: getContentValueLineIndex(keyValueLines, index),
                    });
                }
            });
            result[sv.valueElementId] = markers;
        });
        return result;
    }, [stateValues]);

    const generateMarkers = (stateValue: StateValue) => {
        const breakpointsHitForValue = breakpointsHit?.filter(
            (b) => b.value.id === stateValue.valueElementId,
        );

        const breakLinesForValue = breakLines[stateValue.valueElementId];

        return breakpointsHitForValue
            ?.map((hit) => {
                const marker = breakLinesForValue.find((breakLineForValue) =>
                    hit.value.typeElementPropertyId
                        ? hit.value.typeElementPropertyId === breakLineForValue.id
                        : hit.value.id === breakLineForValue.id,
                );

                if (!marker) {
                    return null;
                }

                return {
                    startRow: marker.line,
                    type: 'fullLine' as MarkerType,
                    className: 'breakpoint-marker',
                    startCol: 0,
                    endRow: 0,
                    endCol: 0,
                };
            })
            .filter((marker): marker is NonNullable<typeof marker> => marker !== null);
    };

    const classes = classNames('full-height', 'flex', 'align-center', 'call-stack-outer', {
        'has-changed': hasStateValuesChanged,
    });

    useEffect(() => {
        if (selectedCallStackItem?.id) {
            setHasStateValuesChanged(true);
            setTimeout(() => {
                setHasStateValuesChanged(false);
            }, 300);
        }
    }, [selectedCallStackItem?.id]);

    // Mirrors the call stack pane's own resizeHorizontally (Debug.tsx) - a fixed pixel floor and a
    // cap expressed as a percentage of the container, ignoring the drag while it's out of bounds
    // rather than clamping to the boundary.
    const onResizeMouseDown = () => {
        // A previous drag's mouseup can be missed (e.g. released outside the browser
        // window), leaving its listeners attached - clean those up before adding new ones.
        activeResizeCleanupRef.current?.();

        const onMouseMove = (event: MouseEvent) => {
            const containerRect = innerRef.current?.getBoundingClientRect();
            if (!containerRect?.width) {
                return;
            }

            const newWidth = event.clientX - containerRect.left;
            const percent = (100 * newWidth) / containerRect.width;

            if (newWidth > DEBUG_LIST_MIN_WIDTH && percent < DEBUG_LIST_MAX_WIDTH_PERCENT) {
                setListWidth(newWidth);
            }
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            activeResizeCleanupRef.current = null;
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        activeResizeCleanupRef.current = onMouseUp;
    };

    return (
        <div data-testid="state-values" className={classes}>
            {selectedCallStackItem && stateValues && selectedStateValue ? (
                <div className="state-values-inner" ref={innerRef}>
                    <div
                        className="state-value-list"
                        data-testid="state-value-list"
                        style={{ width: listWidth }}
                    >
                        {stateValues.map((stateValue) => {
                            const itemClasses = classNames('state-value-item', {
                                'is-selected':
                                    stateValue.valueElementId === selectedStateValue.valueElementId,
                            });

                            return (
                                <button
                                    key={stateValue.valueElementId}
                                    className={itemClasses}
                                    onClick={() =>
                                        setSelectedValueElementId(stateValue.valueElementId)
                                    }
                                    type="button"
                                >
                                    {stateValue.developerName}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        data-testid="state-value-list-resize-handle"
                        className="sub-drag-handle"
                        onMouseDown={onResizeMouseDown}
                        type="button"
                    />
                    <div className="state-value-detail">
                        <CodeEditor
                            // Remount per value so the editor starts at the top of the new
                            // document rather than keeping the previous value's scroll position
                            key={`${selectedStateValue.valueElementId}-${selectedCallStackItem.id}`}
                            readOnly
                            wrapEnabled={false}
                            mode="json"
                            value={JSON.stringify(selectedStateValue, null, '\t')}
                            markers={generateMarkers(selectedStateValue)}
                        />
                    </div>
                </div>
            ) : selectedCallStackItem && stateValues === null ? (
                <div className="center">
                    <Loader message="Loading state values..." />
                </div>
            ) : (
                <div className="center">
                    <ExEmptyState
                        label="There are no state values to display"
                        text="Select a call stack item to view state values."
                    >
                        <ExIcon icon="values-flow" slot="header" />
                    </ExEmptyState>
                </div>
            )}
        </div>
    );
};

export default StateValues;
